import React from 'react';
import { useStyles } from 'components/Statistics/styles';

import StatisticsDownloadButton from 'components/Button/StatisticsDownloadButton';

import { Cell, Pie, PieChart } from 'recharts';
import {
  CHART_WIDTH,
  CHART_HEIGHT,
  PIE_CHART_OUTER_RADIUS,
  INNER_PIE_CHART_OUTER_RADIUS,
  OUTER_PIE_CHART_INNER_RADIUS,
  INNER_PIE_CHART_INNER_RADIUS,
  CHART_COLORS,
  RADIAN,
} from './Statistics';

import Badge from './Badge';
import { bytesToGBOrTB } from './utils';
import EmptyPieChart from './EmptyPieChart';
import { ExportTaskType } from 'commons/vo/param/StatisticsExportParamVO';

type AssetProps = {
  statisticsAssetData: PieChartAssetData[];
  totalAsset: PieTotalAssetData;
  startDate: string;
  endDate: string;
};

function Asset({ statisticsAssetData, totalAsset, startDate, endDate }: AssetProps) {
  const classes = useStyles();

  const hasSizeData = statisticsAssetData.some(v => v.fileSize !== 0);
  const hasCountData = statisticsAssetData.some(v => v.count !== 0);

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitleBox}>
        <div className={classes.sectionTitle}>에셋</div>
        <StatisticsDownloadButton
          taskType={ExportTaskType.Asset}
          startDate={startDate}
          endDate={endDate}
        />
      </div>

      <div className={classes.sectionBody}>
        <div className={classes.pieCharts}>
          <div className={classes.pieChart}>
            {hasSizeData ? (
              <PieChart width={CHART_WIDTH / 2} height={CHART_HEIGHT}>
                <Pie
                  data={statisticsAssetData}
                  dataKey="fileSize"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={INNER_PIE_CHART_OUTER_RADIUS}
                  innerRadius={INNER_PIE_CHART_INNER_RADIUS}
                  labelLine={false}
                  label={renderCustomizedInnerLabel}
                  isAnimationActive={false}
                >
                  <Cell fill={CHART_COLORS.default} />
                  <Cell fill={CHART_COLORS.disable} />
                </Pie>
                <Pie
                  data={statisticsAssetData}
                  dataKey="fileSize"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={PIE_CHART_OUTER_RADIUS}
                  innerRadius={OUTER_PIE_CHART_INNER_RADIUS}
                  label={renderCustomizedOuterLabelSize}
                  isAnimationActive={false}
                >
                  <Cell fill={CHART_COLORS.default} />
                  <Cell fill={CHART_COLORS.disable} />
                </Pie>
              </PieChart>
            ) : (
              <EmptyPieChart />
            )}
            <div
              style={{
                width: INNER_PIE_CHART_INNER_RADIUS * 2,
                height: INNER_PIE_CHART_INNER_RADIUS * 2,
              }}
              className={classes.pieChartCenter}
            >
              {`총 ${bytesToGBOrTB(totalAsset.fileSize)}`}
            </div>
          </div>

          <div className={classes.pieChart}>
            {hasCountData ? (
              <PieChart width={CHART_WIDTH / 2} height={CHART_HEIGHT}>
                <Pie
                  data={statisticsAssetData}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={INNER_PIE_CHART_OUTER_RADIUS}
                  innerRadius={INNER_PIE_CHART_INNER_RADIUS}
                  labelLine={false}
                  label={renderCustomizedInnerLabel}
                  isAnimationActive={false}
                >
                  <Cell fill={CHART_COLORS.default} />
                  <Cell fill={CHART_COLORS.disable} />
                </Pie>
                <Pie
                  data={statisticsAssetData}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={PIE_CHART_OUTER_RADIUS}
                  innerRadius={OUTER_PIE_CHART_INNER_RADIUS}
                  label={renderCustomizedOuterLabel}
                  isAnimationActive={false}
                >
                  <Cell fill={CHART_COLORS.default} />
                  <Cell fill={CHART_COLORS.disable} />
                </Pie>
              </PieChart>
            ) : (
              <EmptyPieChart />
            )}
            <div
              style={{
                width: INNER_PIE_CHART_INNER_RADIUS * 2,
                height: INNER_PIE_CHART_INNER_RADIUS * 2,
              }}
              className={classes.pieChartCenter}
            >{`총 ${totalAsset.count}개`}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderCustomizedInnerLabel({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
}) {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5 - 5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return value === 0 ? null : (
    <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

function renderCustomizedOuterLabel({ cx, cy, midAngle, innerRadius, outerRadius, name, value }) {
  const radius = innerRadius + (outerRadius - innerRadius) + 30;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <>
      <text
        x={x}
        y={y - 8}
        fontSize={12}
        fontWeight={900}
        fill="black"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {name}
      </text>
      <text
        x={x}
        y={y + 8}
        fontSize={10}
        fill="grey"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${value}개`}
      </text>
    </>
  );
}

function renderCustomizedOuterLabelSize({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  name,
  value,
}) {
  const radius = innerRadius + (outerRadius - innerRadius) + 30;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <>
      <text
        x={x}
        y={y - 8}
        fontSize={12}
        fontWeight={900}
        fill="black"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {name}
      </text>
      <text
        x={x}
        y={y + 8}
        fontSize={10}
        fill="grey"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${bytesToGBOrTB(value)}`}
      </text>
    </>
  );
}

export default Asset;
